import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PiggyBank, Info, FilePdf, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { exportRetirementPdf } from "@/lib/retirementPdf";

const COLORS = ["#6366f1", "#10b981", "#38bdf8", "#f59e0b", "#a78bfa", "#f43f5e"];
const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;

export default function RetirementReport() {
  const { token } = useParams();
  const reportRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["retirement-share", token],
    queryFn: async () => (await api.get(`/public/retirement/share/${token}`)).data,
    retry: false,
  });

  const doExportPdf = async () => {
    setPdfBusy(true);
    try {
      await exportRetirementPdf(reportRef.current, { title: data?.name || "Retirement Plan", subtitle: `Shared by ${data?.owner_name || "a MyPayAlert user"}` });
      toast.success("PDF downloaded.");
    } catch { toast.error("Could not generate PDF."); }
    finally { setPdfBusy(false); }
  };

  if (isLoading) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground" data-testid="report-loading">Loading report…</div>;
  if (isError || !data) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center text-center px-6" data-testid="report-notfound">
      <PiggyBank className="h-12 w-12 text-primary mb-4" weight="duotone" />
      <h1 className="text-2xl font-black">Report not found</h1>
      <p className="text-muted-foreground mt-2">This shared retirement report doesn't exist or has expired.</p>
      <Button className="mt-5 rounded-md" onClick={() => window.location.assign("/")} data-testid="report-home">Go to MyPayAlert</Button>
    </div>
  );

  const sim = data.result || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-5 py-8 space-y-6" data-testid="retirement-report">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider text-primary font-bold flex items-center gap-1.5"><PiggyBank className="h-4 w-4" weight="bold" /> MyPayAlert · Shared report</p>
            <h1 className="text-3xl font-black tracking-tight">{data.name}</h1>
            <p className="text-muted-foreground text-sm mt-1">{sim.plan?.name} · Retire at {data.inputs?.retirement_age} · Shared by {data.owner_name}</p>
          </div>
          <Button variant="outline" className="rounded-md" onClick={doExportPdf} disabled={pdfBusy} data-testid="report-export-pdf"><FilePdf className="h-4 w-4 mr-1.5" weight="bold" /> {pdfBusy ? "Generating…" : "Download PDF"}</Button>
        </div>

        <div className="flex items-start gap-2 rounded-md bg-amber-500/10 text-amber-300/90 px-3 py-2 text-xs">
          <Info className="h-4 w-4 shrink-0 mt-0.5" weight="bold" />
          {data.disclaimer}
        </div>

        <div ref={reportRef} className="space-y-5">
          <div className="grid sm:grid-cols-4 gap-3">
            <Card className="p-4 bg-card border-border" data-testid="report-monthly"><p className="text-xs text-muted-foreground">Est. monthly income</p><p className="text-2xl font-black text-emerald-400">{usd(sim.monthly_total)}</p></Card>
            <Card className="p-4 bg-card border-border"><p className="text-xs text-muted-foreground">Est. annual income</p><p className="text-2xl font-black">{usd(sim.annual_total)}</p></Card>
            <Card className="p-4 bg-card border-border"><p className="text-xs text-muted-foreground">Income replacement</p><p className="text-2xl font-black">{sim.replacement_pct ?? 0}%</p></Card>
            <Card className="p-4 bg-card border-border"><p className="text-xs text-muted-foreground">Readiness score</p><p className={`text-2xl font-black ${(sim.readiness_score || 0) >= 70 ? "text-emerald-400" : "text-amber-300"}`}>{sim.readiness_score ?? 0}</p></Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4 bg-card border-border">
              <p className="text-sm font-bold mb-2">Income by source</p>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={sim.sources || []} dataKey="annual" nameKey="label" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.label.split(" ")[0]}`}>
                    {(sim.sources || []).map((s, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "#1e1e28", border: "1px solid #333", borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-4 bg-card border-border">
              <p className="text-sm font-bold mb-2">Monthly income by retirement age</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sim.charts?.retire_age_comparison || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a35" />
                  <XAxis dataKey="age" stroke="#888" fontSize={11} /><YAxis stroke="#888" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "#1e1e28", border: "1px solid #333", borderRadius: 8 }} />
                  <Bar dataKey="monthly" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-4 bg-card border-border md:col-span-2">
              <p className="text-sm font-bold mb-2">Savings growth over your career</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={sim.charts?.savings_growth || []}>
                  <defs><linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a35" />
                  <XAxis dataKey="age" stroke="#888" fontSize={11} /><YAxis stroke="#888" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => usd(v)} contentStyle={{ background: "#1e1e28", border: "1px solid #333", borderRadius: 8 }} />
                  <Area type="monotone" dataKey="balance" stroke="#10b981" fill="url(#g2)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {sim.insights?.length > 0 && (
            <Card className="p-4 bg-card border-border">
              <p className="text-sm font-bold mb-2">Smart insights</p>
              <ul className="space-y-1.5">{sim.insights.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2"><TrendUp className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" weight="bold" /> {t}</li>)}</ul>
            </Card>
          )}
        </div>

        <div className="text-center pt-2">
          <Button variant="ghost" className="text-primary" onClick={() => window.location.assign("/")} data-testid="report-cta">Build your own plan on MyPayAlert →</Button>
        </div>
      </div>
    </div>
  );
}
